// What counts as a breadcrumb — the muted "↳ Drafted email" trace of work okthx
// did on the way to an answer. Pure (types only), and shared because the feed
// relocation logic in `turnBreadcrumbs.ts` needs the same test the renderers use.
import type { GetArtifactResp } from "@/types/artifacts.ts";
import { autoMessageTypes, type GetMessageResp, userMessageTypes } from "@/types/messages.ts";

/**
 * Check if a message has artifacts that would be rendered (heavy or userResponse).
 * This matches the logic in MessageItem.tsx for hasRenderableArtifacts.
 */
export const messageHasRenderableArtifacts = (
  artifacts: GetArtifactResp[] | undefined,
): boolean => {
  return (
    artifacts?.some((artifact) => artifact.isHeavy || artifact.artifactType === "userResponse") ??
    false
  );
};

/**
 * Check if a message would be rendered as a text-only breadcrumb (CornerDownRight icon with muted text).
 * This happens when a non-user, non-auto message has content to display but no renderable artifacts.
 *
 * These messages visually appear as breadcrumbs in the UI, so they should count for
 * the purpose of hiding the ActionBar on preceding milestone messages.
 */
export const messageIsTextOnlyBreadcrumb = (message: GetMessageResp): boolean => {
  // Auto messages are rendered differently (by AutoMessageItem)
  const isAutoMessage = autoMessageTypes.safeParse(message.type).success;
  if (isAutoMessage) return false;

  // User messages (userRequest) are rendered with a bubble on the right
  const isUserMessage = userMessageTypes.safeParse(message.type).success;
  if (isUserMessage) return false;

  // Must have content that should be displayed
  if (!message.content || !message.shouldDisplayContent) return false;

  // Must NOT have renderable artifacts (otherwise it's rendered via ArtifactGroup)
  const hasRenderableArtifacts = messageHasRenderableArtifacts(message.artifacts);
  if (hasRenderableArtifacts) return false;

  return true;
};

/**
 * Check if a set of artifacts would trigger the collapsible breadcrumb section to be shown.
 * When breadcrumbs are shown, we hide the footer (ActionBar) to avoid visual clutter.
 *
 * Only a heavy artifact does. A question form does not: it is not a step okthx
 * took on the way to an answer, it *is* the answer, so it renders like any other
 * reply — its own prose (supplied by the renderer, see `mapUserResponse`) and
 * its own footer, rather than a collapsible row labelled with a message body
 * that a form does not have.
 */
export const artifactsHaveBreadcrumbs = (artifacts: GetArtifactResp[]): boolean =>
  artifacts.some((artifact) => artifact.isHeavy);

/** Whether this message renders as a breadcrumb, either kind. */
export const messageIsBreadcrumb = (message: GetMessageResp): boolean =>
  artifactsHaveBreadcrumbs(message.artifacts ?? []) || messageIsTextOnlyBreadcrumb(message);
